import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { SYRINGE_SPECS, PRESET_DOSES, INPUT_LIMITS } from '../constants/syringeTypes';
import './DosageCalculator.css';

function DosageCalculator({
  setFillLevel,
  syringeType,
  setCurrentDose,
  quickDoseValue,
  onQuickDoseUsed,
}) {
  const [peptideMg, setPeptideMg] = useState('');
  const [bacWaterMl, setBacWaterMl] = useState('');
  const [desiredDoseMg, setDesiredDoseMg] = useState('');
  const [syringeFillMl, setSyringeFillMl] = useState(null);
  const [syringeFillUnits, setSyringeFillUnits] = useState(null);

  // Handle quick dose values from parent
  useEffect(() => {
    if (quickDoseValue) {
      setPeptideMg(quickDoseValue.peptideMg);
      setBacWaterMl(quickDoseValue.bacWaterMl);
      setDesiredDoseMg(quickDoseValue.desiredDoseMg);
      if (onQuickDoseUsed) {
        onQuickDoseUsed();
      }
    }
  }, [quickDoseValue, onQuickDoseUsed]);

  const calculateSyringeFill = useCallback(() => {
    const peptideValue = parseFloat(peptideMg);
    const bacWaterValue = parseFloat(bacWaterMl);
    const doseValue = parseFloat(desiredDoseMg);

    // Guard against division by zero
    if (peptideMg && bacWaterMl && desiredDoseMg && bacWaterValue > 0) {
      const concentration = peptideValue / bacWaterValue;
      const fillMl = doseValue / concentration;

      setSyringeFillMl(fillMl.toFixed(INPUT_LIMITS.DECIMAL_PLACES_DOSE));

      const spec = SYRINGE_SPECS[syringeType];
      const units = (fillMl / spec.ml) * spec.units;
      setSyringeFillUnits(units.toFixed(1));

      const fillPercentage = Math.min((fillMl / spec.ml) * 100, 100);
      setFillLevel(fillPercentage);

      setCurrentDose({
        dose: desiredDoseMg,
        volume: fillMl.toFixed(INPUT_LIMITS.DECIMAL_PLACES_DOSE),
        units: units.toFixed(1),
      });
    } else {
      setSyringeFillMl(null);
      setSyringeFillUnits(null);
      setFillLevel(0);
      setCurrentDose(null);
    }
  }, [peptideMg, bacWaterMl, desiredDoseMg, syringeType, setFillLevel, setCurrentDose]);

  useEffect(() => {
    calculateSyringeFill();
  }, [calculateSyringeFill]);

  const handlePeptideMgChange = (e) => {
    const value = e.target.value;
    if (value === '') {
      setPeptideMg(value);
      return;
    }
    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue >= 0 && numValue <= INPUT_LIMITS.MAX_PEPTIDE_MG) {
      const formattedValue = value.includes('.')
        ? value.split('.')[0] + '.' + value.split('.')[1].slice(0, INPUT_LIMITS.DECIMAL_PLACES_VOLUME)
        : value;
      setPeptideMg(formattedValue);
    }
  };

  const handleBacWaterMlChange = (e) => {
    const value = e.target.value;
    if (value === '') {
      setBacWaterMl(value);
      return;
    }
    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue >= 0 && numValue <= INPUT_LIMITS.MAX_BAC_WATER_ML) {
      const formattedValue = value.includes('.')
        ? value.split('.')[0] + '.' + value.split('.')[1].slice(0, INPUT_LIMITS.DECIMAL_PLACES_VOLUME)
        : value;
      setBacWaterMl(formattedValue);
    }
  };

  const handleDesiredDoseMgChange = (e) => {
    const value = e.target.value;
    // Allow empty string for clearing
    if (value === '') {
      setDesiredDoseMg(value);
      return;
    }
    // Allow typing decimal points and leading zeros
    if (value === '0.' || value === '0.0' || value === '0.00') {
      setDesiredDoseMg(value);
      return;
    }
    // Parse the value
    const numValue = parseFloat(value);
    // Validate range and set value
    if (
      !isNaN(numValue) &&
      numValue >= INPUT_LIMITS.MIN_DOSE_MG &&
      numValue <= INPUT_LIMITS.MAX_DOSE_MG
    ) {
      // Limit to 3 decimal places
      const formattedValue = value.includes('.')
        ? value.split('.')[0] + '.' + value.split('.')[1].slice(0, INPUT_LIMITS.DECIMAL_PLACES_DOSE)
        : value;
      setDesiredDoseMg(formattedValue);
    } else if (numValue === 0 && value === '0') {
      // Allow entering just "0"
      setDesiredDoseMg(value);
    }
  };

  const currentSpec = SYRINGE_SPECS[syringeType];
  const concentration =
    peptideMg && bacWaterMl && parseFloat(bacWaterMl) > 0
      ? (parseFloat(peptideMg) / parseFloat(bacWaterMl)).toFixed(2)
      : null;

  return (
    <div className="calculator-panel">
      <h2 className="panel-title">Dosage Calculator</h2>
      <p className="panel-description">
        Calculate how much to draw into your syringe for your desired dose
      </p>

      <div className="input-row">
        <div className="input-group">
          <label className="input-label">
            Peptide Amount (mg)
            <span className="input-hint">Total in vial</span>
          </label>
          <input
            type="number"
            className="input-field"
            value={peptideMg}
            onChange={handlePeptideMgChange}
            placeholder="e.g., 10"
            min="0"
            max={INPUT_LIMITS.MAX_PEPTIDE_MG}
            step="0.1"
          />
        </div>

        <div className="input-group">
          <label className="input-label">
            Bac Water Added (mL)
            <span className="input-hint">Volume added</span>
          </label>
          <input
            type="number"
            className="input-field"
            value={bacWaterMl}
            onChange={handleBacWaterMlChange}
            placeholder="e.g., 2"
            min="0"
            max={INPUT_LIMITS.MAX_BAC_WATER_ML}
            step="0.1"
          />
        </div>
      </div>

      <div className="input-group">
        <label className="input-label">
          Desired Dose (mg)
          <span className="input-hint">
            Amount you want to inject ({INPUT_LIMITS.MIN_DOSE_MG} - {INPUT_LIMITS.MAX_DOSE_MG}mg)
          </span>
        </label>
        <input
          type="number"
          className="input-field"
          value={desiredDoseMg}
          onChange={handleDesiredDoseMgChange}
          placeholder="e.g., 0.5"
          min={INPUT_LIMITS.MIN_DOSE_MG}
          max={INPUT_LIMITS.MAX_DOSE_MG}
          step="0.001"
        />

        <div className="preset-buttons">
          {PRESET_DOSES.map((dose) => (
            <button
              key={dose.value}
              className="preset-button"
              onClick={() => setDesiredDoseMg(dose.value.toString())}
            >
              {dose.label}
            </button>
          ))}
        </div>
      </div>

      {syringeFillMl && syringeFillUnits && (
        <div className="result-box">
          <div className="result-label">Draw Into Syringe</div>
          <div className="result-grid">
            <div className="result-item">
              <div className="result-value">
                <span className="result-number">{syringeFillMl}</span>
                <span className="result-unit">mL</span>
              </div>
            </div>
            <div className="result-divider">or</div>
            <div className="result-item">
              <div className="result-value">
                <span className="result-number">{syringeFillUnits}</span>
                <span className="result-unit">units</span>
              </div>
            </div>
          </div>

          {parseFloat(syringeFillMl) > currentSpec.ml && (
            <div className="warning-message">
              Volume exceeds selected syringe capacity. Please select a larger syringe.
            </div>
          )}

          {concentration && (
            <div className="result-info">Current concentration: {concentration} mg/mL</div>
          )}
        </div>
      )}

      <div className="calculation-breakdown">
        <h3>Calculation Formula:</h3>
        <div className="formula">Volume (mL) = Desired Dose (mg) ÷ Concentration (mg/mL)</div>
        {concentration && (
          <div className="formula-values">
            Concentration = {peptideMg} mg ÷ {bacWaterMl} mL = {concentration} mg/mL
          </div>
        )}
      </div>
    </div>
  );
}

DosageCalculator.propTypes = {
  setFillLevel: PropTypes.func.isRequired,
  syringeType: PropTypes.string.isRequired,
  setCurrentDose: PropTypes.func.isRequired,
  quickDoseValue: PropTypes.shape({
    peptideMg: PropTypes.number,
    bacWaterMl: PropTypes.number,
    desiredDoseMg: PropTypes.number,
  }),
  onQuickDoseUsed: PropTypes.func,
};

DosageCalculator.defaultProps = {
  quickDoseValue: null,
  onQuickDoseUsed: null,
};

export default DosageCalculator;
